import React, { useEffect, useCallback, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { Markdown } from '@tiptap/markdown'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import { EditorToolbar } from './EditorToolbar'
import { ProcessingHighlight } from './ProcessingHighlightExtension'

import type { BaseSelectionContext } from '~/types/canvas'

interface CanvasEditorProps {
  initialContent: string
  onContentChange: (content: string) => void
  readOnly?: boolean
  placeholder?: string
  /** Callback when user submits feedback on selected text */
  onSelectionFeedback?: (feedback: string, context: BaseSelectionContext) => void
  /** Whether the canvas is currently being processed by the agent */
  isProcessing?: boolean
  /** Optional action element rendered right-aligned at the top of the scroll area */
  actionSlot?: React.ReactNode
}

/**
 * TipTap-based rich text editor for editable canvas items.
 * Features a bubble menu toolbar that appears on text selection (matching Figma design).
 */
export function CanvasEditor({
  initialContent,
  onContentChange,
  readOnly = false,
  placeholder = 'Start writing...',
  onSelectionFeedback,
  isProcessing = false,
  actionSlot
}: CanvasEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
          HTMLAttributes: {
            class: 'font-bold text-tokens-neutral-text-primary'
          }
        },
        codeBlock: {
          HTMLAttributes: {
            class: 'bg-tokens-neutral-bg-grey-2 rounded-lg p-4 font-mono text-sm overflow-x-auto'
          }
        },
        code: {
          HTMLAttributes: {
            class:
              'bg-tokens-neutral-bg-grey-2 px-1.5 py-0.5 rounded text-sm font-mono text-tokens-status-error-text'
          }
        },
        blockquote: {
          HTMLAttributes: {
            class: 'border-l-4 border-tokens-primary-2 pl-4 py-1 text-tokens-neutral-text-secondary'
          }
        },
        bulletList: {
          HTMLAttributes: {
            class: 'list-disc pl-5 space-y-1'
          }
        },
        orderedList: {
          HTMLAttributes: {
            class: 'list-decimal pl-5 space-y-1'
          }
        },
        paragraph: {
          HTMLAttributes: {
            class: 'leading-[16px]'
          }
        }
      }),
      Underline,
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          class:
            'text-tokens-status-link-1 hover:text-tokens-status-link-2 underline cursor-pointer'
        }
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass:
          'before:content-[attr(data-placeholder)] before:text-tokens-neutral-text-placeholder before:float-left before:h-0 before:pointer-events-none'
      }),
      // Processing highlight extension using decorations (works on ALL content including code blocks)
      ProcessingHighlight,
      Table.configure({
        resizable: false,
        HTMLAttributes: {
          class: 'curie-table'
        }
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          class: 'curie-table-header'
        }
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'curie-table-cell'
        }
      }),
      // Markdown extension for parsing/serializing markdown content
      Markdown.configure({
        transformPastedText: true,
        transformCopiedText: true
      })
    ],
    content: initialContent,
    contentType: 'markdown', // Parse initial content as markdown
    editable: !readOnly,
    editorProps: {
      attributes: {
        class:
          'max-w-none focus:outline-none min-h-[400px] text-tokens-neutral-text-secondary font-medium text-sm font-geist leading-[16px]'
      }
    },
    onUpdate: ({ editor }) => {
      // Serialize content back to markdown
      onContentChange(editor.getMarkdown())
    }
  })

  // Update content when initialContent changes externally (e.g., from S3 refetch)
  useEffect(() => {
    if (editor && initialContent !== editor.getMarkdown()) {
      editor.commands.setContent(initialContent, { emitUpdate: false, contentType: 'markdown' })
    }
  }, [initialContent, editor])

  // Update editable state when readOnly changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly)
    }
  }, [readOnly, editor])

  // Track previous isProcessing to detect when processing completes
  const wasProcessingRef = useRef(isProcessing)

  // Clear processing highlight decoration when processing completes
  useEffect(() => {
    const wasProcessing = wasProcessingRef.current
    wasProcessingRef.current = isProcessing

    // Processing just completed (was true, now false)
    if (wasProcessing && !isProcessing && editor) {
      // Remove the processing highlight decoration
      editor.commands.unsetProcessingHighlight()
    }
  }, [isProcessing, editor])

  // BubbleMenu shows when there's a text selection (and not processing)
  const shouldShowBubbleMenu = useCallback(
    ({ from, to }: { editor: typeof editor; from: number; to: number }) => {
      // Hide during processing - the overlay handles the visual feedback
      if (isProcessing) return false
      return from !== to
    },
    [isProcessing]
  )

  // Calculate exact line number in markdown for a document position.
  // Strategy: Insert a unique marker at the position, serialize to markdown,
  // find the marker in markdown and count lines. This is exact, not heuristic.
  const getMarkdownLineForPosition = useCallback(
    (pos: number): number => {
      if (!editor || !editor.markdown || pos <= 1) return 1

      // Generate a unique marker with random hex to avoid any collision with content
      const randomHex = Math.random().toString(16).slice(2, 9)
      const MARKER = `⟦CURIE_${randomHex}_MARKER⟧`

      // Create a transaction that inserts the marker (we won't apply it to the editor)
      const tr = editor.state.tr.insertText(MARKER, pos)
      const tempState = editor.state.apply(tr)

      // Convert the temporary document to JSON and serialize to markdown
      const tempDocJson = tempState.doc.toJSON()

      let markdown: string
      try {
        // Use TipTap's markdown manager to serialize the JSON to markdown
        markdown = editor.markdown.serialize(tempDocJson)
      } catch {
        // Fallback if markdown manager is not available
        return 1
      }

      // Find the marker in the serialized markdown
      const markerIndex = markdown.indexOf(MARKER)
      if (markerIndex === -1) {
        return 1
      }

      // Count newlines before the marker
      const textBeforeMarker = markdown.slice(0, markerIndex)
      const lineNumber = (textBeforeMarker.match(/\n/g) || []).length + 1

      return lineNumber
    },
    [editor]
  )

  // Get selection context with surrounding text and line numbers.
  // Line numbers are calculated from the serialized markdown to match
  // what the agent sees when editing the file.
  const getSelectionContext = useCallback((): BaseSelectionContext | null => {
    if (!editor) return null

    const { from, to } = editor.state.selection
    if (from === to) return null // No selection

    // Get selected text from TipTap (use space as block separator for display)
    const selectedText = editor.state.doc.textBetween(from, to, ' ')
    if (!selectedText.trim()) return null

    // Get context around selection from TipTap
    const CONTEXT_SIZE = 50
    const contextBefore = editor.state.doc.textBetween(Math.max(0, from - CONTEXT_SIZE), from, '\n')
    const contextAfter = editor.state.doc.textBetween(
      to,
      Math.min(editor.state.doc.content.size, to + CONTEXT_SIZE),
      '\n'
    )

    // Calculate exact line numbers from markdown serialization
    const startLine = getMarkdownLineForPosition(from)
    const endLine = getMarkdownLineForPosition(to)

    return {
      selectedText,
      contextBefore,
      contextAfter,
      startLine,
      endLine
    }
  }, [editor, getMarkdownLineForPosition])

  // Handle feedback submission from toolbar
  const handleFeedbackSubmit = useCallback(
    (feedback: string) => {
      if (!onSelectionFeedback) return

      const context = getSelectionContext()
      if (context) {
        // Submit the feedback - this will trigger isProcessing=true from parent
        // Note: EditorToolbar already applied the processing highlight decoration
        onSelectionFeedback(feedback, context)
      }
    },
    [onSelectionFeedback, getSelectionContext]
  )

  return (
    <div className='relative h-full bg-white'>
      {/* Processing overlay - grays out editor and blocks interaction */}
      {isProcessing && <div className='absolute inset-0 bg-white/60 z-10 pointer-events-auto' />}

      {/* Bubble menu toolbar - appears on text selection (hidden during processing) */}
      {editor && !readOnly && !isProcessing && (
        <BubbleMenu
          editor={editor}
          shouldShow={shouldShowBubbleMenu}
          options={{
            placement: 'top'
          }}
        >
          <EditorToolbar
            editor={editor}
            onFeedbackSubmit={onSelectionFeedback ? handleFeedbackSubmit : undefined}
          />
        </BubbleMenu>
      )}

      {/* Scrollable content area */}
      <div className='h-full overflow-auto pl-12 pr-8 py-4 flex flex-col items-start gap-4 flex-1 self-stretch'>
        {actionSlot && <div className='flex justify-end w-full shrink-0'>{actionSlot}</div>}
        <EditorContent
          editor={editor}
          className='[&_.ProseMirror]:min-h-[400px] [&_.ProseMirror]:outline-none [&_.ProseMirror]:flex [&_.ProseMirror]:flex-col [&_.ProseMirror]:justify-center [&_.ProseMirror]:items-start [&_.ProseMirror]:gap-6 [&_.ProseMirror]:flex-1 [&_.ProseMirror_h1]:text-base [&_.ProseMirror_h1]:leading-[18px] [&_.ProseMirror_h2]:text-base [&_.ProseMirror_h2]:leading-[18px] [&_.ProseMirror_h3]:text-base [&_.ProseMirror_h3]:leading-[18px] [&_.processing-highlight]:bg-tokens-primary-4 [&_.processing-highlight]:rounded-sm [&_.curie-table]:my-2 [&_.curie-table]:w-full [&_.curie-table]:border-collapse [&_.curie-table]:table-fixed [&_.curie-table-header]:border [&_.curie-table-header]:border-tokens-neutral-border-2 [&_.curie-table-header]:bg-tokens-neutral-bg-grey-2 [&_.curie-table-header]:px-3 [&_.curie-table-header]:py-2 [&_.curie-table-header]:align-top [&_.curie-table-cell]:border [&_.curie-table-cell]:border-tokens-neutral-border-2 [&_.curie-table-cell]:px-3 [&_.curie-table-cell]:py-2 [&_.curie-table-cell]:align-top'
        />
      </div>
    </div>
  )
}
